import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ScalesIcon as Scales, FileTextIcon as FileText, FilePdfIcon as FilePdf, DownloadSimpleIcon as DownloadSimple, EnvelopeSimpleIcon as EnvelopeSimple, ArchiveIcon as Archive, ArrowSquareOutIcon as ArrowSquareOut, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ListChecksIcon as ListChecks, ClockCounterClockwiseIcon as ClockCounterClockwise, ChatCircleTextIcon as ChatCircleText, PencilIcon as Pencil, FunnelIcon as Funnel, NotePencilIcon as NotePencil, ArrowCounterClockwiseIcon as ArrowCounterClockwise, UploadSimpleIcon as UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RowListSkeleton } from "@/components/SkeletonList";
import { formatApiError, formatDate } from "@/lib/utils-app";

/**
 * Admin · Legal & Compliance Bundle
 *
 * One destination for every legal/compliance/regulatory draft the
 * platform publishes — download per-doc PDF/text, grab the whole
 * thing as a ZIP, email it directly to outside counsel, AND track
 * the review/approval status of each document over time.
 */

const STATUS_OPTIONS = [
  { id: "pending_review", label: "Pending review", tone: "#6B6B6B", bg: "#F4F4F4" },
  { id: "lawyer_approved", label: "Lawyer approved", tone: "#0F8043", bg: "#E8F5EE" },
  { id: "needs_revision", label: "Needs revision", tone: "#C1272D", bg: "#FCE9EA" },
  { id: "published", label: "Published", tone: "#8A6A00", bg: "#FBF7EC" },
];
const STATUS_LOOKUP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.id, s]));

export default function AdminLegalBundle() {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [dispatchesLoading, setDispatchesLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [statusDialog, setStatusDialog] = useState(null); // current item being edited
  const [historyDialog, setHistoryDialog] = useState(null);
  const [editorDialog, setEditorDialog] = useState(null); // static doc CMS editor
  const [refreshingCounts, setRefreshingCounts] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const token = useMemo(() => {
    try { return localStorage.getItem("token") || ""; } catch { return ""; }
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/legal-bundle/review-status");
      setManifest(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  const refreshDispatches = async () => {
    setDispatchesLoading(true);
    try {
      const r = await api.get("/admin/legal-bundle/dispatches", { params: { limit: 25 } });
      setDispatches(r.data?.dispatches || []);
    } catch { /* not critical */ }
    finally { setDispatchesLoading(false); }
  };

  useEffect(() => { refresh(); refreshDispatches(); }, []);

  const filteredItems = useMemo(() => {
    const items = manifest?.items || [];
    if (filter === "all") return items;
    return items.filter((it) => it.status === filter);
  }, [manifest, filter]);

  const grouped = useMemo(() => {
    const out = {};
    filteredItems.forEach((it) => {
      out[it.category] = out[it.category] || [];
      out[it.category].push(it);
    });
    return out;
  }, [filteredItems]);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const docHref = (id, ext) =>
    `${apiBase}/api/admin/legal-bundle/doc/${id}.${ext}?auth=${encodeURIComponent(token)}`;
  const zipHref = `${apiBase}/api/admin/legal-bundle/zip?auth=${encodeURIComponent(token)}`;

  const downloadAll = () => {
    setBundleBusy(true);
    // Browser handles the download; clear busy flag after a short moment.
    window.location.href = zipHref;
    setTimeout(() => setBundleBusy(false), 1200);
  };

  const refreshPageCounts = async () => {
    setRefreshingCounts(true);
    try {
      const r = await api.post("/admin/legal-bundle/refresh-page-counts", {});
      toast.success(
        `Refreshed page counts for ${r.data?.refreshed_count || 0} document${
          (r.data?.refreshed_count || 0) === 1 ? "" : "s"
        }${r.data?.failed_count ? ` · ${r.data.failed_count} failed` : ""}.`
      );
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshingCounts(false);
    }
  };

  const publishToStorage = async () => {
    if (!window.confirm(
      "Render every doc and publish to persistent storage?\n\n" +
      "After this completes, production will serve all legal docs " +
      "directly from object storage — no on-demand Chromium rendering, " +
      "no Cloudflare bot-protection issues. Takes ~25 seconds."
    )) return;
    setPublishing(true);
    try {
      const r = await api.post("/admin/legal-bundle/publish-to-storage", {});
      const counts = r.data?.counts || {};
      toast.success(
        `Published ${counts.published || 0} doc${(counts.published || 0) === 1 ? "" : "s"} to storage${
          counts.failed ? ` · ${counts.failed} failed` : ""
        }.`
      );
      if ((counts.failed || 0) > 0) {
        const failedList = (r.data?.failed || [])
          .map((f) => `${f.label}: ${f.error}`)
          .join("\n");
        // eslint-disable-next-line no-console
        console.warn("Failed docs:\n" + failedList);
      }
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPublishing(false);
    }
  };



  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.2em] text-crimson font-bold flex items-center gap-1.5 mb-2">
          <Scales size={12} weight="fill" /> Admin · Legal & compliance
        </p>
        <h1 className="font-spectral text-4xl tracking-tight">Legal & compliance bundle</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
          Every public-facing legal, compliance, and regulatory document the
          Registry publishes — in one place. Download individually as PDF or
          plain text, grab everything as a single ZIP, or email the bundle
          directly to outside counsel for review.
        </p>
      </div>

      <div className="rounded-2xl bg-[#0E0E0E] text-white p-5 sm:p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold mb-2 flex items-center gap-1.5">
            <Archive size={12} weight="fill" /> The whole library
          </p>
          <p className="text-[14px] leading-relaxed text-white/85">
            One ZIP containing every document in both PDF and plain-text form,
            plus a manifest README with version + effective dates. Bundle is
            generated on-demand — always reflects the current published copy.
          </p>
        </div>
        <div className="flex flex-col gap-2 md:items-end justify-center">
          <Button
            onClick={downloadAll}
            disabled={bundleBusy || loading}
            data-testid="legal-bundle-download-zip"
            className="rounded-full bg-[#C9A227] hover:bg-white hover:text-ink text-ink font-semibold w-full md:w-auto"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            {bundleBusy ? "Building ZIP…" : "Download all as ZIP"}
          </Button>
          <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="legal-bundle-email-open"
                className="rounded-full border-white/40 text-white bg-transparent hover:bg-white/10 hover:text-white w-full md:w-auto"
              >
                <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
                Email to lawyer
              </Button>
            </DialogTrigger>
            <EmailDialogContent onSent={() => { setEmailOpen(false); refreshDispatches(); }} />
          </Dialog>
          {(manifest?.missing_page_counts ?? 0) > 0 && (
            <Button
              onClick={refreshPageCounts}
              disabled={refreshingCounts || loading}
              variant="outline"
              data-testid="legal-bundle-refresh-page-counts"
              className="rounded-full border-white/40 text-white bg-transparent hover:bg-white/10 hover:text-white w-full md:w-auto"
              title={`${manifest.missing_page_counts} document${manifest.missing_page_counts === 1 ? " is" : "s are"} missing a page count — click to render them all and cache the counts (~60s).`}
            >
              {refreshingCounts ? "Refreshing… ~60s" : `Refresh page counts (${manifest.missing_page_counts})`}
            </Button>
          )}
          <Button
            onClick={publishToStorage}
            disabled={publishing || loading}
            data-testid="legal-bundle-publish-to-storage"
            className="rounded-full bg-[#C9A227] hover:bg-[#A88819] text-ink font-semibold w-full md:w-auto"
            title="Render every doc and push to object storage so production serves them instantly without Chromium. Run this on preview after any legal-doc edit."
          >
            {publishing ? "Publishing… ~25s" : "Publish bundle to storage"}
          </Button>
        </div>
      </div>

      {loading && <RowListSkeleton />}

      {!loading && manifest && (
        <>
          <div
            className="rounded-2xl border border-[#E5E5E5] bg-white px-4 py-4 mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid="review-status-counts"
          >
            <div className="flex flex-wrap items-center gap-2">
              {STATUS_OPTIONS.map((s) => {
                const n = manifest.counts?.[s.id] || 0;
                const active = filter === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setFilter(active ? "all" : s.id)}
                    data-testid={`review-status-pill-${s.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors ${
                      active ? "border-[#0E0E0E] ring-2 ring-[#0E0E0E]/15" : "border-[#E5E5E5] hover:border-[#0E0E0E]/40"
                    }`}
                    style={{ background: s.bg }}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: s.tone }} />
                    <span className="text-[12px] font-semibold" style={{ color: s.tone }}>{s.label}</span>
                    <span className="text-[12px] font-bold text-ink">{n}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 text-[12px] text-[#6B6B6B]">
              <Funnel size={12} weight="duotone" />
              {filter === "all" ? "Showing all documents" : (
                <button
                  type="button"
                  onClick={() => setFilter("all")}
                  className="underline hover:text-ink"
                  data-testid="review-status-clear-filter"
                >
                  Showing {STATUS_LOOKUP[filter]?.label.toLowerCase()} only · clear filter
                </button>
              )}
            </div>
          </div>

          <section data-testid="legal-bundle-list">
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="mb-7">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6A00] font-bold flex items-center gap-1.5 mb-3">
                  <ListChecks size={12} weight="fill" /> {category}
                </p>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <DocRow
                      key={it.id}
                      item={it}
                      pdfHref={docHref(it.id, "pdf")}
                      txtHref={docHref(it.id, "txt")}
                      onEditStatus={() => setStatusDialog(it)}
                      onShowHistory={() => setHistoryDialog(it)}
                      onEditContent={() => setEditorDialog(it)}
                    />
                  ))}
                </ul>
              </div>
            ))}
            {filteredItems.length === 0 && (
              <p className="text-sm text-[#6B6B6B] py-6 text-center" data-testid="legal-bundle-empty-filter">
                No documents in this status. <button type="button" className="underline" onClick={() => setFilter("all")}>Show all</button>
              </p>
            )}
            <p className="text-[11px] text-[#8B8B8B] mt-2">
              {manifest.total} document{manifest.total === 1 ? "" : "s"} total
            </p>
          </section>
        </>
      )}

      <section className="mt-12" data-testid="legal-bundle-dispatches">
        <h2 className="font-spectral text-2xl tracking-tight mb-3">Sent to counsel</h2>
        {dispatchesLoading ? (
          <RowListSkeleton />
        ) : dispatches.length === 0 ? (
          <p className="text-sm text-[#6B6B6B]" data-testid="legal-bundle-dispatches-empty">
            No bundles have been emailed yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {dispatches.map((d) => (
              <li
                key={d.id}
                data-testid={`dispatch-${d.id}`}
                className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink truncate">
                    {d.to_email}
                    {d.cc_self && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.14em] font-bold text-[#8A6A00] bg-[#FBF7EC] px-2 py-0.5 rounded-full">
                        cc'd self
                      </span>
                    )}
                  </p>
                  <p className="text-[12px] text-[#6B6B6B]">
                    {formatDate(d.sent_at)} · by {d.sent_by_email || d.sent_by} · {d.doc_count} docs · {(d.bundle_bytes / 1024).toFixed(0)} KB
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[12px]">
                  {d.delivered === false || (!d.resend_id && !d.cc_resend_id) ? (
                    <span className="text-crimson font-bold flex items-center gap-1">
                      <Warning size={12} weight="fill" /> Resend not configured
                    </span>
                  ) : (
                    <span className="text-[#0F8043] font-bold flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> Delivered
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ReviewStatusDialog
        item={statusDialog}
        open={!!statusDialog}
        onClose={() => setStatusDialog(null)}
        onSaved={() => { setStatusDialog(null); refresh(); }}
      />
      <ReviewHistoryDialog
        item={historyDialog}
        open={!!historyDialog}
        onClose={() => setHistoryDialog(null)}
      />
      <ContentEditorDialog
        item={editorDialog}
        open={!!editorDialog}
        onClose={() => setEditorDialog(null)}
        onSaved={() => refresh()}
      />
    </AppShell>
  );
}

function DocRow({ item, pdfHref, txtHref, onEditStatus, onShowHistory, onEditContent }) {
  const status = STATUS_LOOKUP[item.status] || STATUS_LOOKUP.pending_review;
  const stale =
    item.kind === "dynamic" &&
    item.status === "lawyer_approved" &&
    item.version_snapshot &&
    item.version &&
    item.version_snapshot !== item.version;
  const isStaticEditable = item.kind === "static";
  return (
    <li
      data-testid={`legal-doc-${item.id}`}
      className="rounded-xl border border-[#E5E5E5] bg-white p-4 flex flex-col md:flex-row md:items-start gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-spectral text-lg text-ink tracking-tight">{item.label}</h3>
          {item.version && (
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-crimson bg-[#C1272D]/10 px-2 py-0.5 rounded-full">
              {item.version}
            </span>
          )}
          {item.kind === "dynamic" && (
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#8A6A00] bg-[#FBF7EC] px-2 py-0.5 rounded-full">
              Versioned
            </span>
          )}
          {item.kind === "static" && item.override_published && (
            <span
              data-testid={`legal-doc-override-${item.id}`}
              className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#0F8043] bg-[#E8F5EE] px-2 py-0.5 rounded-full"
            >
              CMS published
            </span>
          )}
          {item.page_count > 0 && (
            <span
              data-testid={`legal-doc-pages-${item.id}`}
              className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#3F3F3F] bg-[#F4F1E8] border border-[#E5E0CC] px-2 py-0.5 rounded-full"
              title={item.byte_size ? `${Math.round(item.byte_size / 1024)} KB` : ""}
            >
              {item.page_count} {item.page_count === 1 ? "page" : "pages"}
            </span>
          )}
          <span
            data-testid={`legal-doc-status-${item.id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold"
            style={{ background: status.bg, color: status.tone }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: status.tone }} />
            {status.label}
          </span>
          {stale && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold text-crimson bg-[#FCE9EA]"
              data-testid={`legal-doc-stale-${item.id}`}
              title={`Approved against ${item.version_snapshot}, current is ${item.version}`}
            >
              <Warning size={10} weight="fill" />
              Re-review
            </span>
          )}
        </div>
        <p className="text-[13px] text-[#3F3F3F] leading-relaxed mt-1">{item.description}</p>
        {item.status_notes && (
          <p
            data-testid={`legal-doc-notes-${item.id}`}
            className="text-[12px] italic text-[#3F3F3F] bg-[#FAFAFA] border-l-2 border-[#C9A227] pl-2 py-1 mt-1.5 whitespace-pre-wrap"
          >
            {item.status_notes}
          </p>
        )}
        <p className="text-[11px] text-[#8B8B8B] mt-1.5 flex items-center gap-1.5 flex-wrap">
          {item.last_updated ? `Last updated ${item.last_updated}` : "—"}
          {item.reviewed_at && (
            <>
              {" · "}reviewed {formatDate(item.reviewed_at)}
              {item.reviewed_by_email && <> by {item.reviewed_by_email}</>}
            </>
          )}
          {item.lawyer_email && (
            <>
              {" · "}counsel: {item.lawyer_email}
            </>
          )}
          {item.route && (
            <>
              {" · "}
              <Link
                to={item.route}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-ink"
                data-testid={`legal-doc-view-${item.id}`}
              >
                <span className="inline-flex items-center gap-1">
                  Preview <ArrowSquareOut size={10} />
                </span>
              </Link>
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap md:justify-end">
        <a href={pdfHref} download data-testid={`legal-doc-pdf-${item.id}`}>
          <Button variant="outline" size="sm" className="rounded-full border-[#E5E5E5]">
            <FilePdf size={13} weight="duotone" className="mr-1.5 text-crimson" />
            PDF
          </Button>
        </a>
        <a href={txtHref} download data-testid={`legal-doc-txt-${item.id}`}>
          <Button variant="outline" size="sm" className="rounded-full border-[#E5E5E5]">
            <FileText size={13} weight="duotone" className="mr-1.5 text-ink" />
            Text
          </Button>
        </a>
        {isStaticEditable && (
          <Button
            variant="outline"
            size="sm"
            onClick={onEditContent}
            data-testid={`legal-doc-edit-${item.id}`}
            className="rounded-full border-[#C9A227] text-[#8A6A00] hover:bg-[#FBF7EC]"
          >
            <NotePencil size={13} weight="duotone" className="mr-1.5" />
            Edit
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onEditStatus}
          data-testid={`legal-doc-status-edit-${item.id}`}
          className="rounded-full border-[#0E0E0E] text-ink hover:bg-[#0E0E0E] hover:text-white"
        >
          <Pencil size={12} weight="bold" className="mr-1.5" />
          Status
        </Button>
        {item.status_set && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowHistory}
            data-testid={`legal-doc-history-${item.id}`}
            className="rounded-full text-[#6B6B6B] hover:text-ink"
          >
            <ClockCounterClockwise size={13} weight="duotone" />
          </Button>
        )}
      </div>
    </li>
  );
}

function EmailDialogContent({ onSent }) {
  const [toEmail, setToEmail] = useState("");
  const [note, setNote] = useState("");
  const [ccSelf, setCcSelf] = useState(true);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!toEmail.trim()) {
      toast.error("Enter the lawyer's email.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/legal-bundle/email", {
        to_email: toEmail.trim(),
        cc_self: ccSelf,
        note: note.trim() || undefined,
      });
      const updates = r.data?.status_updates || 0;
      const flipMsg = updates ? ` · ${updates} doc${updates === 1 ? "" : "s"} flipped to "pending review"` : "";
      if (r.data?.delivered) {
        toast.success(`Bundle sent to ${toEmail}${flipMsg}`);
      } else {
        toast.warning(`Bundle built but Resend not configured${flipMsg}`);
      }
      onSent && onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <DialogContent data-testid="legal-bundle-email-dialog" className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-spectral text-2xl tracking-tight">Email bundle to outside counsel</DialogTitle>
        <DialogDescription className="text-[13.5px] leading-relaxed">
          We'll build the full ZIP (PDF + plain text for every document) and
          send it as an attachment with a branded cover note. <strong>Every
          document in the bundle is automatically flipped to "Pending review"
          and stamped with this dispatch ID</strong>, so the audit trail stays
          self-updating.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <label htmlFor="lb-email-to" className="text-[12px] font-semibold text-ink mb-1 block">
            Lawyer's email
          </label>
          <Input
            id="lb-email-to"
            type="email"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            placeholder="counsel@firm.com"
            data-testid="legal-bundle-email-to"
          />
        </div>
        <div>
          <label htmlFor="lb-email-note" className="text-[12px] font-semibold text-ink mb-1 block">
            Optional note (shown above the document list)
          </label>
          <Textarea
            id="lb-email-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Hi — please flag any items that need updates before our next compliance review."
            rows={4}
            maxLength={4000}
            data-testid="legal-bundle-email-note"
          />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-[#3F3F3F] cursor-pointer">
          <Checkbox
            checked={ccSelf}
            onCheckedChange={(v) => setCcSelf(Boolean(v))}
            data-testid="legal-bundle-email-cc-self"
          />
          Also send a copy to my admin email
        </label>
      </div>
      <DialogFooter>
        <Button
          onClick={send}
          disabled={busy}
          data-testid="legal-bundle-email-send"
          className="rounded-full bg-[#0E0E0E] hover:bg-[#C9A227] hover:text-ink text-white"
        >
          <EnvelopeSimple size={14} weight="bold" className="mr-1.5" />
          {busy ? "Sending…" : "Send bundle"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}


function ReviewStatusDialog({ item, open, onClose, onSaved }) {
  const [status, setStatus] = useState(item?.status || "pending_review");
  const [notes, setNotes] = useState(item?.status_notes || "");
  const [lawyerEmail, setLawyerEmail] = useState(item?.lawyer_email || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) {
      setStatus(item.status || "pending_review");
      setNotes(item.status_notes || "");
      setLawyerEmail(item.lawyer_email || "");
    }
  }, [item]);

  if (!item) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/legal-bundle/review-status/${item.id}`, {
        status,
        notes: notes.trim() || undefined,
        lawyer_email: lawyerEmail.trim() || undefined,
      });
      toast.success(`${item.label} → ${STATUS_LOOKUP[status]?.label || status}`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="review-status-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl tracking-tight">
            Review status · {item.label}
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-relaxed">
            Track the document through your lawyer-review workflow. The status
            is captured against the current version snapshot ({item.version || "static"}),
            so future edits surface as "needs re-review".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-ink mb-1 block">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="review-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`review-status-option-${s.id}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-ink mb-1 block">
              Notes <span className="text-[#8B8B8B] font-normal">(optional — visible to admins only)</span>
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Lawyer flagged section 4 — needs to remove the 'arbitrator must be in NY' clause."
              rows={4}
              maxLength={4000}
              data-testid="review-status-notes"
            />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-ink mb-1 block">
              Reviewing counsel (optional)
            </label>
            <Input
              type="email"
              value={lawyerEmail}
              onChange={(e) => setLawyerEmail(e.target.value)}
              placeholder="counsel@firm.com"
              data-testid="review-status-lawyer-email"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="review-status-cancel" className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="review-status-save"
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C9A227] hover:text-ink text-white"
          >
            {busy ? "Saving…" : "Save status"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewHistoryDialog({ item, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item || !open) return;
    setLoading(true);
    api.get(`/admin/legal-bundle/review-status/${item.id}/history`)
      .then((r) => setRows(r.data?.history || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [item, open]);

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="review-history-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl tracking-tight flex items-center gap-2">
            <ClockCounterClockwise size={20} weight="duotone" className="text-gold" />
            History · {item.label}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Append-only log of every status change, in reverse chronological order.
          </DialogDescription>
        </DialogHeader>
        {loading && <RowListSkeleton />}
        {!loading && rows.length === 0 && (
          <p className="text-sm text-[#6B6B6B]" data-testid="review-history-empty">
            No status changes recorded yet.
          </p>
        )}
        {!loading && rows.length > 0 && (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
            {rows.map((r) => {
              const s = STATUS_LOOKUP[r.status];
              return (
                <li
                  key={r.id}
                  data-testid={`review-history-row-${r.id}`}
                  className="rounded-lg border border-[#E5E5E5] bg-white p-3"
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {s && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold"
                        style={{ background: s.bg, color: s.tone }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.tone }} />
                        {s.label}
                      </span>
                    )}
                    {r.version_snapshot && (
                      <span className="text-[10px] text-[#8B8B8B]">v{r.version_snapshot}</span>
                    )}
                    <span className="text-[11px] text-[#6B6B6B] ml-auto">
                      {formatDate(r.reviewed_at)} by {r.reviewed_by_email || r.reviewed_by}
                    </span>
                  </div>
                  {r.lawyer_email && (
                    <p className="text-[11px] text-[#3F3F3F] mb-1">
                      Counsel: <strong>{r.lawyer_email}</strong>
                    </p>
                  )}
                  {r.linked_dispatch_id && (
                    <p className="text-[11px] text-[#8A6A00] mb-1">
                      Triggered by dispatch <code className="bg-[#FBF7EC] px-1 py-0.5 rounded">{r.linked_dispatch_id}</code>
                    </p>
                  )}
                  {r.notes && (
                    <p className="text-[12px] italic text-[#3F3F3F] bg-[#FAFAFA] border-l-2 border-[#C9A227] pl-2 py-1 whitespace-pre-wrap">
                      <ChatCircleText size={11} weight="duotone" className="inline mr-1 text-gold" />
                      {r.notes}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContentEditorDialog({ item, open, onClose, onSaved }) {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState("");
  const [versionInput, setVersionInput] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [versionNotes, setVersionNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [previewHtml, setPreviewHtml] = useState("");

  const load = async () => {
    if (!item) return;
    try {
      const r = await api.get(`/admin/legal-pages/${item.id}`);
      setState(r.data);
      setDraft(r.data?.draft_markdown || r.data?.current_markdown || "");
      setVersionInput("");
      setEffectiveDate("");
      setVersionNotes("");
    } catch (err) {
      toast.error(formatApiError(err));
      onClose && onClose();
    }
  };

  useEffect(() => {
    if (open && item) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  useEffect(() => {
    if (!showPreview) return;
    setPreviewHtml(mdPreview(draft));
  }, [draft, showPreview]);

  // File-input ref for the "Import from Word / PDF" button. Must live
  // ABOVE the `if (!item) return null` early-return so the hook order
  // stays stable between renders (React hook rule).
  const fileInputRef = useRef(null);

  if (!item) return null;

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/legal-pages/${item.id}/draft`, { markdown: draft });
      toast.success("Draft saved");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!draft.trim()) { toast.error("Draft is empty."); return; }
    if (!confirm(`Publish a new version of ${item.label}? This becomes live immediately on the public page.`)) return;
    setBusy(true);
    try {
      await api.put(`/admin/legal-pages/${item.id}/draft`, { markdown: draft });
      const r = await api.post(`/admin/legal-pages/${item.id}/publish`, {
        version: versionInput.trim() || undefined,
        effective_date: effectiveDate.trim() || undefined,
        version_notes: versionNotes.trim() || undefined,
      });
      toast.success(`Published ${r.data?.version || "new version"}`);
      onSaved && onSaved();
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!confirm(`Revert ${item.label} to the JSX fallback? The CMS content is preserved and can be re-published later.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/legal-pages/${item.id}/unpublish`);
      toast.success("Reverted to JSX fallback");
      onSaved && onSaved();
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const revertToVersion = async (version) => {
    setBusy(true);
    try {
      await api.post(`/admin/legal-pages/${item.id}/revert/${encodeURIComponent(version)}`);
      toast.success(`Loaded ${version} into draft`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  // Import .docx / .pdf: convert server-side to markdown and replace
  // the draft. We DON'T auto-publish — the imported markdown lands
  // in the editor so the admin can preview + tweak before going live.
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Clear the input so re-uploading the same file fires onChange.
    e.target.value = "";
    if (draft.trim() && !confirm(
      `Replace the current draft with the imported contents of "${f.name}"?\n\n` +
      "Your existing draft will be overwritten. The currently-published " +
      "version on the public page is not affected until you click Publish.",
    )) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post(
        `/admin/legal-pages/${item.id}/import`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const md = r.data?.markdown || "";
      setDraft(md);
      toast.success(
        `Imported ${r.data?.char_count?.toLocaleString() || md.length} characters ` +
        `from ${r.data?.filename || f.name}. Preview, then Publish when ready.`,
      );
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="content-editor-dialog"
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl tracking-tight flex items-center gap-2">
            <NotePencil size={20} weight="duotone" className="text-gold" />
            Edit · {item.label}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Markdown editor with live preview. <strong>Save draft</strong> stores
            your changes without going live; <strong>Publish</strong> bumps the
            version and updates the public {item.route} page immediately. The
            JSX fallback always remains in code as a safety net.
          </DialogDescription>
        </DialogHeader>

        {state && (
          <>
            <div className="rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-[12px] flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                <strong>Status:</strong>{" "}
                {state.is_published ? (
                  <span className="text-[#0F8043] font-bold">Published</span>
                ) : (
                  <span className="text-[#6B6B6B] font-bold">JSX fallback active</span>
                )}
              </span>
              {state.current_version && (
                <span><strong>Live version:</strong> {state.current_version}</span>
              )}
              {state.current_effective_date && (
                <span><strong>Effective:</strong> {state.current_effective_date}</span>
              )}
              {state.draft_updated_at && (
                <span><strong>Draft updated:</strong> {formatDate(state.draft_updated_at)}</span>
              )}
            </div>

            <div className="mt-3 rounded-lg border border-[#C9A227]/40 bg-[#FBF7EC] p-3 flex flex-col sm:flex-row sm:items-center gap-2.5">
              <UploadSimple size={18} weight="duotone" className="text-[#8A6A00] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] font-semibold text-ink">
                  Import from Word / PDF
                </p>
                <p className="text-[11.5px] text-[#6B6B6B] leading-snug">
                  Upload your lawyer's <code>.docx</code> or <code>.pdf</code> —
                  we convert it to clean markdown and drop it into the draft
                  below. Nothing goes live until you click <strong>Publish</strong>.
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onImportFile}
                className="hidden"
                data-testid="content-editor-import-input"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
                data-testid="content-editor-import-button"
                className="rounded-full border-[#C9A227] text-[#8A6A00] hover:bg-[#F2E9C7] shrink-0"
              >
                <UploadSimple size={13} weight="bold" className="mr-1.5" />
                Choose file…
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[12px] font-semibold text-ink mb-1 block flex items-center justify-between">
                  <span>Markdown source</span>
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-[11px] underline text-[#6B6B6B] hover:text-ink"
                    data-testid="content-editor-toggle-preview"
                  >
                    {showPreview ? "Hide preview" : "Show preview"}
                  </button>
                </label>
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={20}
                  className="font-mono text-[13px] leading-[1.5]"
                  placeholder={"## Section heading\n\nPlain paragraph. **Bold** and *italic* work. [Links](https://example.com).\n\n- Bullet\n- Another bullet"}
                  data-testid="content-editor-textarea"
                />
              </div>
              {showPreview && (
                <div>
                  <label className="text-[12px] font-semibold text-ink mb-1 block">Live preview</label>
                  <div
                    data-testid="content-editor-preview"
                    className="border border-[#E5E5E5] rounded-md bg-white p-4 max-h-[480px] overflow-y-auto prose prose-neutral max-w-none text-[13.5px] leading-[1.65]
                      [&_h2]:font-spectral [&_h2]:text-xl [&_h2]:tracking-tight [&_h2]:mt-4 [&_h2]:mb-2
                      [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-1
                      [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ul]:mb-2
                      [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1 [&_ol]:mb-2
                      [&_a]:text-crimson [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: previewHtml || "<em style='color:#6B6B6B'>Empty draft</em>" }}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div>
                <label className="text-[12px] font-semibold text-ink mb-1 block">Version</label>
                <Input
                  value={versionInput}
                  onChange={(e) => setVersionInput(e.target.value)}
                  placeholder={state.current_version ? `auto: ${nextVersion(state.current_version)}` : "auto: v1.0"}
                  data-testid="content-editor-version"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-ink mb-1 block">Effective date</label>
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  data-testid="content-editor-effective"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-ink mb-1 block">Changelog notes</label>
                <Input
                  value={versionNotes}
                  onChange={(e) => setVersionNotes(e.target.value)}
                  placeholder="What changed?"
                  data-testid="content-editor-notes"
                />
              </div>
            </div>

            {Array.isArray(state.history) && state.history.length > 0 && (
              <div className="mt-3 border-t border-[#E5E5E5] pt-3">
                <p className="text-[12px] font-semibold text-ink mb-2">Version history</p>
                <ul className="space-y-1.5 max-h-[140px] overflow-y-auto">
                  {state.history.slice().reverse().map((h) => (
                    <li
                      key={h.version}
                      data-testid={`content-editor-history-${h.version}`}
                      className="flex items-center justify-between gap-3 text-[12px] bg-[#FAFAFA] border border-[#E5E5E5] rounded px-3 py-1.5"
                    >
                      <div className="min-w-0 flex-1">
                        <strong>{h.version}</strong>
                        {h.effective_date && <> · effective {h.effective_date}</>}
                        {h.published_by_email && <> · by {h.published_by_email}</>}
                        {h.version_notes && (
                          <span className="text-[#6B6B6B] italic ml-1.5">— {h.version_notes}</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revertToVersion(h.version)}
                        data-testid={`content-editor-revert-${h.version}`}
                        className="text-[#8A6A00] hover:text-ink"
                      >
                        <ArrowCounterClockwise size={12} weight="bold" className="mr-1" />
                        Load to draft
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:justify-between mt-4">
          {state?.is_published && (
            <Button
              variant="outline"
              onClick={unpublish}
              disabled={busy}
              data-testid="content-editor-unpublish"
              className="rounded-full border-[#C1272D] text-crimson hover:bg-[#FCE9EA]"
            >
              Revert to JSX
            </Button>
          )}
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <Button
              variant="outline"
              onClick={saveDraft}
              disabled={busy}
              data-testid="content-editor-save-draft"
              className="rounded-full"
            >
              {busy ? "Saving…" : "Save draft"}
            </Button>
            <Button
              onClick={publish}
              disabled={busy}
              data-testid="content-editor-publish"
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C9A227] hover:text-ink text-white"
            >
              {busy ? "Publishing…" : "Publish version"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function nextVersion(prev) {
  if (!prev) return "v1.0";
  const m = /^v(\d+)\.(\d+)$/.exec(prev.trim());
  if (!m) return `${prev.trim()}.1`;
  return `v${m[1]}.${parseInt(m[2], 10) + 1}`;
}

// Tiny browser-side markdown→HTML matching the server converter.
// Used for the live preview only — the canonical render still happens
// server-side at publish time so the public page never trusts client HTML.
function mdPreview(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
  const lines = md.split(/\r?\n/);
  const out = [];
  let inUl = false, inOl = false, paraBuf = [];
  const flushPara = () => {
    const txt = paraBuf.join(" ").trim();
    if (txt) out.push(`<p>${inline(txt)}</p>`);
    paraBuf = [];
  };
  const closeLists = () => {
    if (inUl) { out.push("</ul>"); inUl = false; }
    if (inOl) { out.push("</ol>"); inOl = false; }
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/g, "");
    if (!line.trim()) { flushPara(); closeLists(); continue; }
    if (/^\s*---+\s*$/.test(line)) { flushPara(); closeLists(); out.push("<hr/>"); continue; }
    let m = /^(#{1,6})\s+(.*)$/.exec(line);
    if (m) {
      flushPara(); closeLists();
      const tag = m[1].length <= 2 ? "h2" : "h3";
      out.push(`<${tag}>${inline(m[2].trim())}</${tag}>`);
      continue;
    }
    m = /^\s*[-*]\s+(.*)$/.exec(line);
    if (m) {
      flushPara();
      if (inOl) { out.push("</ol>"); inOl = false; }
      if (!inUl) { out.push("<ul>"); inUl = true; }
      out.push(`<li>${inline(m[1].trim())}</li>`);
      continue;
    }
    m = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (m) {
      flushPara();
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (!inOl) { out.push("<ol>"); inOl = true; }
      out.push(`<li>${inline(m[1].trim())}</li>`);
      continue;
    }
    paraBuf.push(line.trim());
  }
  flushPara(); closeLists();
  return out.join("\n");
}

